<div class="min-h-screen bg-[#f5f5f5] py-4 px-3 sm:py-6 sm:px-4">
  <div class="max-w-[1020px] mx-auto space-y-3 sm:space-y-4">

    <!-- Back navigation -->
    <button (click)="navigateBack()"
      class="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#D31148] transition-colors">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 12H5" />
        <path d="M12 19l-7-7 7-7" />
      </svg>
      Back
    </button>

    <!-- ── Error Banner ── -->
    <div *ngIf="saveError"
      class="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center justify-between">
      <span>{{ saveError }}</span>
      <button (click)="saveError = ''" class="ml-4 text-red-400 hover:text-red-600 text-lg leading-none">&times;</button>
    </div>

    <!-- ── Draft Warning Banner ── -->
    <div *ngIf="draftWarning"
      class="px-4 py-3 bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded-xl flex items-start justify-between gap-3">
      <div class="flex items-start gap-2.5">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span>{{ draftWarning }}</span>
      </div>
      <button (click)="draftWarning = ''" class="text-amber-500 hover:text-amber-700 text-lg leading-none flex-shrink-0">&times;</button>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- CARD 1 — Title + Actions                              -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div class="bg-white rounded-2xl border-2 border-[#D31148] px-4 py-4 sm:px-6 shadow-sm">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <input
              [(ngModel)]="title"
              type="text"
              placeholder="Title of Blog"
              [attr.maxlength]="MAX_TITLE_LENGTH"
              class="flex-1 text-xl sm:text-[28px] leading-tight font-extrabold text-gray-900 placeholder-gray-300 outline-none border-none bg-transparent min-w-0 w-full"
            />
            <div class="dd-host relative flex-shrink-0">
              <button type="button" (click)="toggleTitleEmojiPicker(); $event.stopPropagation()"
                [class.text-brand-500]="showTitleEmojiPicker"
                class="flex h-9 w-9 items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 transition-colors"
                title="Emoji">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </button>
              <app-emoji-picker [isOpen]="showTitleEmojiPicker" (pick)="pickTitleEmoji($event)" />
            </div>
          </div>
          <p class="mt-1 text-xs" [class.text-error-500]="title.length >= MAX_TITLE_LENGTH" [class.text-gray-400]="title.length < MAX_TITLE_LENGTH">
            {{ title.length }}/{{ MAX_TITLE_LENGTH }}
          </p>
        </div>
        <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            *ngIf="!editBlogId || editingBlogStatus === 'DRAFT'"
            (click)="saveDraft()"
            [disabled]="isSaving || isPublishing"
            class="flex-1 sm:flex-none justify-center px-3 sm:px-5 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            <svg *ngIf="isSaving" class="w-3.5 h-3.5 animate-spin text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            {{ isSaving ? 'Saving…' : 'Save as draft' }}
          </button>
          <button
            (click)="publish()"
            [disabled]="isSaving || isPublishing || (requiresConsent && !blogConsentChecked)"
            class="flex-1 sm:flex-none justify-center px-3 sm:px-6 py-2 text-sm font-semibold text-white bg-[#D31148] rounded-xl hover:bg-[#b80e3c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            <svg *ngIf="isPublishing" class="w-3.5 h-3.5 animate-spin text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            {{ isPublishing ? 'Publishing…' : (editBlogId && editingBlogStatus !== 'DRAFT' ? 'Save Changes' : 'Publish') }}
          </button>
        </div>
      </div>
      <!-- Consent checkbox: required whenever this blog has never been published
           before (create mode, or editing a draft). Already-PUBLISHED blogs skip
           it since consent was already certified on first publish. -->
      <label *ngIf="requiresConsent" class="flex items-start gap-3 mt-3 pt-3 border-t border-gray-100 cursor-pointer group">
        <input type="checkbox" [(ngModel)]="blogConsentChecked" name="blogConsentChecked"
          class="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-[#D31148] cursor-pointer" />
        <span class="text-xs text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors">
          I certify that everyone in the picture / video consented to have it uploaded to Safeword, that everyone was 18 or older when it was taken and I have documentation to support this.
        </span>
      </label>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- CARD 2 — Toolbar + Editor                             -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div class="bg-white rounded-2xl border-2 border-[#D31148] shadow-sm overflow-hidden">
      <div class="flex">

        <!-- Toolbar + Editor -->
        <div class="flex-1 min-w-0">

          <!-- Toolbar wrapper -->
          <div class="border-2 border-[#D31148] rounded-xl m-2 sm:m-3 mb-0">

            <!-- Row 1 -->
            <div #toolbarRow class="toolbar-row flex flex-nowrap sm:flex-wrap items-center gap-0.5 px-2 py-1.5 overflow-x-auto">

              <!-- Undo / Redo -->
              <button (click)="exec('undo')" title="Undo" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7v6h6"/><path d="M3 13A9 9 0 1 0 6 6.7L3 7"/></svg>
              </button>
              <button (click)="exec('redo')" title="Redo" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 7v6h-6"/><path d="M21 13A9 9 0 1 1 18 6.7L21 7"/></svg>
              </button>

              <!-- Copy / Cut -->
              <button (click)="exec('copy')" title="Copy" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
              <button (click)="exec('cut')" title="Cut" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="6" cy="20" r="2"/><circle cx="18" cy="20" r="2"/><path d="M20 4 8 13.5M4 4l4 4.5"/><path d="M8.5 13.5 6 18M15.5 13.5 18 18"/></svg>
              </button>

              <span class="tb-div"></span>

              <!-- Zoom -->
              <div class="flex items-center border border-gray-200 rounded-md overflow-hidden bg-white h-[28px] flex-shrink-0">
                <button (click)="zoom(-10)" class="px-1.5 h-full hover:bg-gray-100 text-gray-500 text-sm border-r border-gray-200">−</button>
                <span class="px-2 text-[11px] font-medium text-gray-700 min-w-[40px] text-center select-none">{{ zoomLevel }}%</span>
                <button (click)="zoom(10)"  class="px-1.5 h-full hover:bg-gray-100 text-gray-500 text-sm border-l border-gray-200">+</button>
              </div>

              <span class="tb-div"></span>

              <!-- Paragraph Style -->
              <div class="dd-host relative flex-shrink-0">
                <button #paragraphBtn (click)="toggleDD('paragraph', paragraphBtn, toolbarRow)" class="dd-btn min-w-[110px]">
                  <span class="truncate">{{ selectedParagraph }}</span>
                  <svg class="w-3 h-3 ml-1 shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <div *ngIf="showParagraphDD" class="dd-menu" [style.top.px]="ddPos.top" [style.left.px]="ddPos.left">
                  <button *ngFor="let s of paragraphStyles" (click)="setParagraph(s)" class="dd-item" [class.dd-active]="selectedParagraph === s.label">{{ s.label }}</button>
                </div>
              </div>

              <span class="tb-div"></span>

              <!-- Font Family -->
              <div class="dd-host relative flex-shrink-0">
                <button #fontBtn (click)="toggleDD('font', fontBtn, toolbarRow)" class="dd-btn min-w-[80px]">
                  <span class="truncate" [style.font-family]="selectedFont">{{ selectedFont }}</span>
                  <svg class="w-3 h-3 ml-1 shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                <div *ngIf="showFontDD" class="dd-menu" [style.top.px]="ddPos.top" [style.left.px]="ddPos.left">
                  <button *ngFor="let f of fontFamilies" (click)="setFont(f)" class="dd-item" [style.font-family]="f" [class.dd-active]="selectedFont === f">{{ f }}</button>
                </div>
              </div>

              <span class="tb-div"></span>

              <!-- Font Size -->
              <div class="flex items-center border border-gray-200 rounded-md overflow-hidden h-[28px] flex-shrink-0">
                <button (click)="decSize()" class="px-1 h-full hover:bg-gray-100 text-gray-500 text-sm border-r border-gray-200">−</button>
                <div class="dd-host relative flex-shrink-0">
                  <button #sizeBtn (click)="toggleDD('size', sizeBtn, toolbarRow)" class="px-2 h-[28px] text-xs font-medium text-gray-700 hover:bg-gray-50 min-w-[28px] text-center">{{ selectedSize }}</button>
                  <div *ngIf="showSizeDD" class="dd-menu w-16 max-h-40" [style.top.px]="ddPos.top" [style.left.px]="ddPos.left">
                    <button *ngFor="let s of fontSizes" (click)="setSize(s)" class="dd-item" [class.dd-active]="selectedSize === s">{{ s }}</button>
                  </div>
                </div>
                <button (click)="incSize()" class="px-1 h-full hover:bg-gray-100 text-gray-500 text-sm border-l border-gray-200">+</button>
              </div>

              <span class="tb-div"></span>

              <!-- Bold -->
              <button (click)="exec('bold')" [class.tb-on]="isBold" title="Bold" class="tb font-bold text-sm">B</button>

              <span class="tb-div"></span>

              <!-- Italic -->
              <button (click)="exec('italic')" [class.tb-on]="isItalic" title="Italic" class="tb italic text-sm">I</button>

              <span class="tb-div"></span>

              <!-- Underline -->
              <button (click)="exec('underline')" [class.tb-on]="isUnderline" title="Underline" class="tb underline text-sm">U</button>

              <span class="tb-div"></span>

              <!-- Strikethrough -->
              <button (click)="exec('strikethrough')" [class.tb-on]="isStrike" title="Strikethrough" class="tb line-through text-sm">S</button>

              <span class="tb-div"></span>

              <!-- Text Color -->
              <button (click)="colorPicker.click()" title="Text Color" class="tb flex flex-col items-center justify-center gap-[2px]">
                <span class="text-sm font-bold leading-none" [style.color]="currentColor">A</span>
                <span class="w-4 h-[3px] rounded-sm" [style.background]="currentColor"></span>
              </button>
              <input #colorPicker type="color" class="sr-only" [value]="currentColor" (input)="setColor(colorPicker.value)" />

              <!-- BG Color -->
              <button (click)="bgColorPicker.click()" title="Highlight Color" class="tb flex flex-col items-center justify-center gap-[2px]">
                <span class="text-sm font-bold leading-none text-gray-500">A</span>
                <span class="w-4 h-[3px] rounded-sm border border-gray-300" [style.background]="currentBgColor"></span>
              </button>
              <input #bgColorPicker type="color" class="sr-only" [value]="currentBgColor" (input)="setBgColor(bgColorPicker.value)" />

              <span class="tb-div"></span>

              <!-- Link / Unlink -->
              <button (click)="openLink()" title="Insert Link" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              </button>
              <button (click)="exec('unlink')" title="Remove Link" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
              </button>

              <span class="tb-div"></span>

              <!-- Emoji -->
              <div class="dd-host relative flex-shrink-0">
                <button #descEmojiBtn type="button" (click)="toggleDescEmojiPicker(descEmojiBtn, toolbarRow); $event.stopPropagation()"
                  [class.tb-on]="showDescEmojiPicker" title="Emoji" class="tb">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                    <line x1="9" y1="9" x2="9.01" y2="9"/>
                    <line x1="15" y1="9" x2="15.01" y2="9"/>
                  </svg>
                </button>
                <app-emoji-picker [isOpen]="showDescEmojiPicker" [anchorTop]="emojiDDPos.top" [anchorLeft]="emojiDDPos.left" (pick)="pickDescEmoji($event)" />
              </div>

              <!-- Hidden file inputs for photo/video -->
              <input #imageInput type="file" accept="image/*,.jfif,.heic,.heif,video/*" class="sr-only" (change)="onImageFile($event)" />
              <input #photoInput type="file" accept="image/*,.jfif,.heic,.heif" class="sr-only" (change)="onImageFile($event)" />
              <input #videoInput type="file" accept="video/*"         class="sr-only" (change)="onImageFile($event)" />

              <!-- <span class="tb-div"></span> -->

              <!-- Sub / Super -->
              <!-- <button (click)="exec('subscript')"   title="Subscript"   class="tb text-xs">X<sub>2</sub></button>
              <button (click)="exec('superscript')" title="Superscript" class="tb text-xs">X<sup>2</sup></button> -->

              <!-- <span class="tb-div"></span> -->

              <!-- Clear / Table / Code / Quote / HR -->
              <button (click)="exec('removeFormat')" title="Clear Format" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M4 12h10"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="21" y1="15" x2="15" y2="21"/></svg>
              </button>
              <button (click)="showTableModal = true" title="Insert Table" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
              </button>
              <!-- <button (click)="insertCode()" title="Code Block" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              </button>
              <button (click)="insertQuote()" title="Blockquote" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/></svg>
              </button> -->
              <button (click)="exec('insertHorizontalRule')" title="Divider" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="2" y1="12" x2="22" y2="12"/></svg>
              </button>

              <span class="tb-div"></span>

              <button (click)="exec('insertUnorderedList')" title="Bullet List" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><circle cx="4" cy="6" r="1" fill="currentColor"/><circle cx="4" cy="12" r="1" fill="currentColor"/><circle cx="4" cy="18" r="1" fill="currentColor"/></svg>
              </button>
              <button (click)="exec('insertOrderedList')" title="Numbered List" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/></svg>
              </button>

              <span class="tb-div"></span>

              <!-- Indent / Outdent -->
              <button (click)="exec('outdent')" title="Decrease Indent" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="8" x2="21" y2="8"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="16" x2="21" y2="16"/><polyline points="7 10 4 12 7 14 7 10"/></svg>
              </button>
              <button (click)="exec('indent')" title="Increase Indent" class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="8" x2="21" y2="8"/><line x1="11" y1="12" x2="21" y2="12"/><line x1="3" y1="16" x2="21" y2="16"/><polyline points="7 10 7 14 10 12 7 10"/></svg>
              </button>

              <span class="tb-div"></span>

              <!-- Alignment -->
              <button (click)="exec('justifyLeft')"   title="Align Left"    class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
              </button>
              <button (click)="exec('justifyCenter')" title="Align Center"  class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
              </button>
              <button (click)="exec('justifyRight')"  title="Align Right"   class="tb">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
              </button>

              <span class="tb-div"></span>

              <!-- Add Photo -->
              <button type="button" (click)="insertPhoto()"
                class="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-[30px] rounded-lg bg-[#D31148] text-white text-xs font-semibold hover:bg-[#b80e3c] transition-colors flex-shrink-0">
                <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span class="hidden sm:inline">Add Photo</span>
              </button>

              <!-- Add Video -->
              <button type="button" (click)="insertVideo()"
                class="inline-flex items-center gap-1.5 px-2.5 sm:px-3 h-[30px] rounded-lg bg-[#D31148] text-white text-xs font-semibold hover:bg-[#b80e3c] transition-colors flex-shrink-0">
                <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="6" width="15" height="12" rx="2"/>
                  <path d="M17 10l5-3v10l-5-3V10z"/>
                </svg>
                <span class="hidden sm:inline">Add Video</span>
              </button>
            </div>

          </div>

          <!-- Editor body -->
          <div class="px-3 py-3 sm:px-4 sm:py-4">
            <div class="relative">
              <p *ngIf="!editorHasContent"
                class="absolute top-0 left-0 text-gray-400 pointer-events-none select-none text-[15px] leading-[1.75]">
                Start typing here...
              </p>
              <div
                #editorBody
                contenteditable="true"
                class="editor-prose outline-none min-h-[320px] sm:min-h-[460px] text-[15px] leading-[1.75] text-gray-800"
                [style.zoom]="zoomLevel + '%'"
                (keyup)="updateState()"
                (mouseup)="updateState()"
                (input)="onEditorInput()"
                (paste)="onEditorPaste()"
                (click)="onEditorClick($event)"
              ></div>
            </div>

            <!-- Open Graph preview of the first link in the blog body.
                 Card only — the editor above is untouched. -->
            <app-link-preview [text]="editorHtml" [isHtml]="true"></app-link-preview>
          </div>
        </div>

      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- CARD 3 — Tag / Collab / Hashtags / Comments           -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div class="bg-white rounded-2xl border-2 border-[#D31148] px-3 py-4 sm:px-5 shadow-sm space-y-1">

      <!-- Tag People -->
      <div data-blog-tag-section>
        <div
          class="flex items-center justify-between py-3 px-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
          (click)="toggleBlogTagPeople()">
          <div class="flex items-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-gray-500 flex-shrink-0">
              <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="currentColor" opacity="0.7"/>
            </svg>
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-700">Tag people</span>
              <span *ngIf="blogTaggedPeople.length > 0"
                class="text-xs font-semibold text-[#D31148] bg-rose-50 px-2 py-0.5 rounded-full">{{ blogTaggedPeople.length }}</span>
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
            class="text-gray-400 flex-shrink-0 transition-transform"
            [class.rotate-90]="showBlogTagPeople">
            <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div *ngIf="showBlogTagPeople" class="mt-1 mb-2 space-y-2 px-2">
          <div *ngIf="blogTaggedPeople.length > 0" class="flex flex-wrap gap-2">
            <span *ngFor="let u of blogTaggedPeople"
              class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 pl-1 pr-2 py-1 text-xs font-medium text-gray-700">
              <img *ngIf="u.profileImage?.urlSmall" [src]="u.profileImage!.urlSmall" class="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              <span *ngIf="!u.profileImage?.urlSmall"
                class="w-5 h-5 rounded-full bg-[#FFD4E0] flex items-center justify-center text-[#D31148] text-[9px] font-bold flex-shrink-0">{{ u.displayName.charAt(0) }}</span>
              @{{ u.userName }}
              <button type="button" (click)="removeBlogTag(u._id)" class="text-gray-400 hover:text-gray-600 leading-none font-bold">×</button>
            </span>
          </div>
          <div class="relative">
            <input type="text" [(ngModel)]="blogTagSearchInput" name="blogTagSearchInput"
              placeholder="Search anyone by name or username…"
              (input)="onBlogTagSearchInput($any($event.target).value)"
              (focus)="blogTagInputFocused = true" (blur)="onBlogTagInputBlur()"
              class="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-[#D31148] transition-colors placeholder:text-gray-400" />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/>
              <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <div *ngIf="blogTagInputFocused && blogTagSearchResults.length > 0"
            class="rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <button *ngFor="let u of blogTagSearchResults" type="button" (mousedown)="addBlogTag(u)"
              class="flex w-full items-center gap-3 px-3 py-2.5 hover:bg-[#fff5f7] transition-colors text-left">
              <div class="flex-shrink-0">
                <img *ngIf="u.profileImage?.urlSmall" [src]="u.profileImage!.urlSmall" class="w-8 h-8 rounded-full object-cover border border-gray-100" />
                <div *ngIf="!u.profileImage?.urlSmall" class="w-8 h-8 rounded-full bg-[#FFD4E0] flex items-center justify-center text-[#D31148] font-bold text-sm flex-shrink-0">{{ u.displayName.charAt(0).toUpperCase() }}</div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-semibold text-gray-800 truncate">{{ u.displayName }}</div>
                <div class="text-xs text-gray-400 truncate">@{{ u.userName }}</div>
              </div>
              <span *ngIf="u.isFriend" class="flex-shrink-0 text-[10px] font-semibold text-[#10b981] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Friend</span>
            </button>
          </div>
          <p *ngIf="blogTagInputFocused && blogTagSearchInput.length > 0 && blogTagSearchResults.length === 0"
            class="text-xs text-gray-400 text-center py-2">No users found for "{{ blogTagSearchInput }}"</p>
        </div>
      </div>

      <div class="h-px bg-gray-100 mx-1"></div>

      <!-- Invite Collaborators -->
      <div data-blog-collab-section>
        <div
          class="flex items-center justify-between py-3 px-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
          (click)="toggleBlogCollaborators()">
          <div class="flex items-center gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-gray-500 flex-shrink-0">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.6"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-700">{{ editBlogId ? 'Manage Collaborators' : 'Invite Collaborators' }}</span>
              <span *ngIf="blogCollaborators.length > 0"
                class="text-xs font-semibold text-[#D31148] bg-rose-50 px-2 py-0.5 rounded-full">{{ blogCollaborators.length }}</span>
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
            class="text-gray-400 flex-shrink-0 transition-transform"
            [class.rotate-90]="showBlogCollaborators">
            <path d="M6 12L10 8L6 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div *ngIf="showBlogCollaborators" class="mt-1 mb-2 space-y-2 px-2">
          <p *ngIf="!editBlogId" class="text-xs text-gray-400">Invite people you follow to co-author this blog. They'll get a notification to accept or decline.</p>
          <p *ngIf="editBlogId" class="text-xs text-gray-400">Tap × to remove a collaborator. Add new ones by searching below.</p>
          <div *ngIf="blogCollaborators.length > 0" class="flex flex-wrap gap-2">
            <span *ngFor="let u of blogCollaborators"
              class="inline-flex items-center gap-1.5 rounded-full border pl-1 pr-2 py-1 text-xs font-medium"
              [class.bg-emerald-50]="blogCollabStatus.get(u._id) === 'ACCEPTED'"
              [class.border-emerald-200]="blogCollabStatus.get(u._id) === 'ACCEPTED'"
              [class.text-emerald-700]="blogCollabStatus.get(u._id) === 'ACCEPTED'"
              [class.bg-amber-50]="blogCollabStatus.get(u._id) === 'PENDING'"
              [class.border-amber-200]="blogCollabStatus.get(u._id) === 'PENDING'"
              [class.text-amber-700]="blogCollabStatus.get(u._id) === 'PENDING'"
              [class.bg-rose-50]="!blogCollabStatus.get(u._id) || blogCollabStatus.get(u._id) === 'DECLINED'"
              [class.border-rose-200]="!blogCollabStatus.get(u._id) || blogCollabStatus.get(u._id) === 'DECLINED'"
              [class.text-[#D31148]]="!blogCollabStatus.get(u._id) || blogCollabStatus.get(u._id) === 'DECLINED'">
              <img *ngIf="u.profileImage?.urlSmall" [src]="u.profileImage!.urlSmall" class="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              <span *ngIf="!u.profileImage?.urlSmall"
                class="w-5 h-5 rounded-full bg-[#FFD4E0] flex items-center justify-center text-[#D31148] text-[9px] font-bold flex-shrink-0">{{ u.displayName.charAt(0) }}</span>
              @{{ u.userName }}
              <span *ngIf="editBlogId && blogCollabStatus.get(u._id)"
                class="text-[9px] font-bold px-1 py-0.5 rounded-full leading-none"
                [class.bg-emerald-100]="blogCollabStatus.get(u._id) === 'ACCEPTED'"
                [class.text-emerald-700]="blogCollabStatus.get(u._id) === 'ACCEPTED'"
                [class.bg-amber-100]="blogCollabStatus.get(u._id) === 'PENDING'"
                [class.text-amber-700]="blogCollabStatus.get(u._id) === 'PENDING'"
                [class.bg-rose-100]="blogCollabStatus.get(u._id) === 'DECLINED'"
                [class.text-rose-600]="blogCollabStatus.get(u._id) === 'DECLINED'">
                {{ blogCollabStatus.get(u._id) === 'ACCEPTED' ? '✓ Accepted' : blogCollabStatus.get(u._id) === 'PENDING' ? '⏳ Pending' : '✗ Declined' }}
              </span>
              <button type="button" (click)="removeBlogCollaborator(u._id)"
                class="leading-none font-bold ml-0.5 hover:opacity-70"
                [class.text-emerald-500]="blogCollabStatus.get(u._id) === 'ACCEPTED'"
                [class.text-amber-500]="blogCollabStatus.get(u._id) === 'PENDING'"
                [class.text-rose-400]="!blogCollabStatus.get(u._id) || blogCollabStatus.get(u._id) === 'DECLINED'">×</button>
            </span>
          </div>
          <div class="relative">
            <input type="text" [(ngModel)]="blogCollabSearchInput" name="blogCollabSearchInput"
              placeholder="Search your friends…"
              (input)="onBlogCollabSearchInput($any($event.target).value)"
              (focus)="blogCollabInputFocused = true" (blur)="onBlogCollabInputBlur()"
              class="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-[#D31148] transition-colors placeholder:text-gray-400" />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/>
              <path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </div>
          <div *ngIf="blogCollabInputFocused && blogCollabSearchResults.length > 0"
            class="rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <button *ngFor="let u of blogCollabSearchResults" type="button" (mousedown)="addBlogCollaborator(u)"
              class="flex w-full items-center gap-3 px-3 py-2.5 hover:bg-[#fff5f7] transition-colors text-left">
              <div class="flex-shrink-0">
                <img *ngIf="u.profileImage?.urlSmall" [src]="u.profileImage!.urlSmall" class="w-8 h-8 rounded-full object-cover border border-gray-100" />
                <div *ngIf="!u.profileImage?.urlSmall" class="w-8 h-8 rounded-full bg-[#FFD4E0] flex items-center justify-center text-[#D31148] font-bold text-sm flex-shrink-0">{{ u.displayName.charAt(0).toUpperCase() }}</div>
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-semibold text-gray-800 truncate">{{ u.displayName }}</div>
                <div class="text-xs text-gray-400 truncate">@{{ u.userName }}</div>
              </div>
              <span *ngIf="u.isFriend" class="flex-shrink-0 text-[10px] font-semibold text-[#10b981] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Friend</span>
            </button>
          </div>
          <p *ngIf="blogCollabInputFocused && blogCollabSearchInput.length > 0 && blogCollabSearchResults.length === 0"
            class="text-xs text-gray-400 text-center py-2">No results. You can only invite people you follow.</p>
          <p *ngIf="blogCollabInputFocused && blogCollabSearchInput.length === 0 && blogCollabSearchResults.length === 0"
            class="text-xs text-gray-400 text-center py-2">You don't follow anyone yet, or everyone is already invited.</p>
        </div>
      </div>

      <div class="h-px bg-gray-100 mx-1"></div>

      <!-- Hashtags -->
      <div class="py-2 px-3">
        <div class="flex items-center gap-2 mb-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-500">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
          </svg>
          <span class="text-sm font-medium text-gray-700">Hashtags</span>
        </div>
        <div *ngIf="blogHashtags.length > 0" class="flex flex-wrap gap-2 mb-2">
          <span *ngFor="let tag of blogHashtags"
            class="inline-flex items-center gap-1.5 rounded-full bg-[#D31148] px-3 py-1 text-xs font-semibold text-white">
            #{{ tag }}
            <button type="button" (click)="removeBlogHashtag(tag)"
              class="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white text-[#D31148] font-bold leading-none text-[10px] hover:bg-white/90">×</button>
          </span>
        </div>
        <div class="relative">
          <input type="text" appHashtagInput #blogHashDir="appHashtagInput"
            [(ngModel)]="blogHashtagInput" name="blogHashtagInput"
            (hashtagSearch)="onBlogHashSearch($event)"
            (hashtagDropdown)="blogHashDropOpen = $event"
            (keydown.enter)="addBlogHashtag($event)"
            (keydown.space)="addBlogHashtag($event)" (separatorTyped)="addBlogHashtag($event)"
            (keydown.comma)="addBlogHashtag($event)"
            placeholder="Add hashtags (e.g. technology)"
            class="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#D31148] transition-colors placeholder:text-gray-400" />
          <app-hashtag-dropdown
            [isOpen]="blogHashDropOpen"
            [tags]="blogHashSuggestions"
            position="above"
            (selected)="onBlogHashSelected($event)">
          </app-hashtag-dropdown>
        </div>
        <p class="mt-1.5 text-xs text-gray-400">Press Enter, Space - or pick a suggestion</p>
      </div>

      <div class="h-px bg-gray-100 mx-1"></div>

      <!-- Allow Comments -->
      <div class="flex items-center justify-between py-3 px-3 rounded-xl">
        <div class="flex items-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-gray-500 flex-shrink-0">
            <path d="M21 12C21 16.9706 16.9706 21 12 21C10.8029 21 9.6603 20.7663 8.61549 20.3419C8.41552 20.2607 8.31554 20.2201 8.23472 20.202C8.15566 20.1843 8.09715 20.1778 8.01613 20.1778C7.9333 20.1778 7.84309 20.1928 7.66265 20.2229L4.10476 20.8159C3.73218 20.878 3.54589 20.909 3.41118 20.8512C3.29328 20.8007 3.19933 20.7067 3.14876 20.5888C3.09098 20.4541 3.12203 20.2678 3.18413 19.8952L3.77711 16.3374C3.80718 16.1569 3.82222 16.0667 3.82221 15.9839C3.8222 15.9028 3.81572 15.8443 3.798 15.7653C3.77988 15.6845 3.73927 15.5845 3.65806 15.3845C3.23374 14.3397 3 13.1971 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <div>
            <span class="text-sm font-medium text-gray-700">Allow Comments</span>
            <p class="text-xs text-gray-400">{{ blogCommentsDisabled ? 'Comments are off' : 'Anyone can comment' }}</p>
          </div>
        </div>
        <button type="button" (click)="blogCommentsDisabled = !blogCommentsDisabled"
          class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0"
          [class.bg-[#D31148]]="!blogCommentsDisabled"
          [class.bg-gray-300]="blogCommentsDisabled">
          <span class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200"
            [class.translate-x-6]="!blogCommentsDisabled"
            [class.translate-x-1]="blogCommentsDisabled">
          </span>
        </button>
      </div>

    </div>

  </div>
</div>

<!-- ── Link Modal ── -->
<div *ngIf="showLinkModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
  <div class="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
    <h3 class="text-base font-semibold text-gray-900 mb-4">Insert Link</h3>
    <label class="block text-sm font-medium text-gray-700 mb-1">URL</label>
    <input [(ngModel)]="linkUrl" type="text" placeholder="https://example.com"
      class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-[#D31148]" />
    <div class="flex justify-end gap-3">
      <button (click)="showLinkModal = false" class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
      <button (click)="insertLink()" class="px-4 py-2 text-sm font-medium text-white bg-[#D31148] rounded-lg hover:opacity-90">Insert</button>
    </div>
  </div>
</div>

<!-- ── Table Modal ── -->
<div *ngIf="showTableModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
  <div class="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
    <h3 class="text-base font-semibold text-gray-900 mb-4">Insert Table</h3>
    <div class="grid grid-cols-2 gap-4 mb-5">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Rows</label>
        <input [(ngModel)]="tableRows" type="number" min="1" max="20" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D31148]" />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Columns</label>
        <input [(ngModel)]="tableCols" type="number" min="1" max="20" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D31148]" />
      </div>
    </div>
    <div class="flex justify-end gap-3">
      <button (click)="showTableModal = false" class="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
      <button (click)="insertTable()" class="px-4 py-2 text-sm font-medium text-white bg-[#D31148] rounded-lg hover:opacity-90">Insert</button>
    </div>
  </div>
</div>

<style>
  :host .tb {
    display: inline-flex; align-items: center; justify-content: center;
    width: 30px; height: 30px; border-radius: 5px; color: #4b5563;
    background: transparent; border: none; cursor: pointer;
    transition: background 0.1s, color 0.1s; flex-shrink: 0;
  }
  :host .tb:hover { background: #f3f4f6; color: #111827; }
  :host .tb-on    { background: #ffe4ec !important; color: #D31148 !important; }
  :host .tb-div   { display: inline-block; width: 1px; height: 18px; background: #e5e7eb; margin: 0 2px; flex-shrink: 0; align-self: center; }

  :host .dd-btn {
    display: flex; align-items: center; height: 28px; padding: 0 8px;
    font-size: 12px; font-weight: 500; color: #374151;
    border: 1px solid #e5e7eb; border-radius: 5px;
    background: #fff; cursor: pointer; white-space: nowrap; overflow: hidden;
  }
  :host .dd-btn:hover { background: #f9fafb; }

  :host .dd-menu {
    position: fixed; z-index: 200;
    background: #fff; border: 1px solid #e5e7eb; border-radius: 8px;
    box-shadow: 0 6px 20px rgba(0,0,0,.1); min-width: 120px;
    padding: 4px 0; max-height: 200px; overflow-y: auto;
  }
  :host .dd-item {
    display: block; width: 100%; text-align: left; padding: 6px 12px;
    font-size: 12px; color: #374151; background: none; border: none;
    cursor: pointer; white-space: nowrap;
  }
  :host .dd-item:hover { background: #f9fafb; }
  :host .dd-active { background: #fff0f4 !important; color: #D31148; font-weight: 500; }

  ::ng-deep .editor-prose p             { margin-bottom: 1rem; }
  ::ng-deep .editor-prose h1            { font-size: 2rem;     font-weight: 700; margin-bottom: .75rem; color: #111827; }
  ::ng-deep .editor-prose h2            { font-size: 1.5rem;   font-weight: 700; margin-bottom: .75rem; color: #111827; }
  ::ng-deep .editor-prose h3            { font-size: 1.25rem;  font-weight: 600; margin-bottom: .5rem;  color: #111827; }
  ::ng-deep .editor-prose h4            { font-size: 1.125rem; font-weight: 600; margin-bottom: .5rem;  color: #111827; }
  ::ng-deep .editor-prose ul            { list-style: disc;    padding-left: 1.5rem; margin-bottom: 1rem; }
  ::ng-deep .editor-prose ol            { list-style: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
  ::ng-deep .editor-prose li            { margin-bottom: .25rem; }
  ::ng-deep .editor-prose a             { color: #D31148; text-decoration: underline; }
  ::ng-deep .editor-prose blockquote    { border-left: 4px solid #D31148; padding: 8px 16px; margin: 1rem 0; color: #555; font-style: italic; background: #fff5f7; }
  ::ng-deep .editor-prose pre           { background: #f4f4f4; padding: 12px 16px; border-radius: 6px; font-family: monospace; font-size: .875rem; margin-bottom: 1rem; border: 1px solid #e5e7eb; overflow-x: auto; }
  ::ng-deep .editor-prose img           { max-width: 100%; height: auto; border-radius: 6px; margin: 1rem 0; }
  ::ng-deep .editor-prose table         { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
  ::ng-deep .editor-prose td,
  ::ng-deep .editor-prose th            { border: 1px solid #d1d5db; padding: 8px 12px; }
  ::ng-deep .editor-prose th            { background: #f9fafb; font-weight: 600; }
  ::ng-deep .editor-prose hr            { border: none; border-top: 2px solid #e5e7eb; margin: 1.5rem 0; }

  ::ng-deep .blog-media-remove {
    position: absolute; top: 8px; right: 8px;
    width: 28px; height: 28px; border-radius: 9999px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0,0,0,0.6); color: #fff; border: none;
    cursor: pointer; opacity: 0; transition: opacity 0.15s, background 0.15s;
  }
  ::ng-deep .blog-media-wrap:hover .blog-media-remove { opacity: 1; }
  ::ng-deep .blog-media-remove:hover { background: #D31148; }
  ::ng-deep .dd-menu::-webkit-scrollbar       { width: 4px; }
  ::ng-deep .dd-menu::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }

  :host .toolbar-row { scrollbar-width: thin; -webkit-overflow-scrolling: touch; }
  :host .toolbar-row::-webkit-scrollbar       { height: 4px; }
  :host .toolbar-row::-webkit-scrollbar-thumb { background: #e5c8d0; border-radius: 2px; }
</style>